import { useEffect, useState } from "react";
import {
  parseContext,
  contextRequest,
  type Context,
  type Project,
  type SourceParameters,
} from "@proof/core";
import type { BulkGenerationServices } from "./bulk-generation";

/** One preview path for proofing and new bulk reviews; stale requests never win. */
export function useBulkContext(
  project: Project,
  partId: string,
  demoProfile: string,
  enabled = true,
  versionId?: string,
  services?: BulkGenerationServices,
) {
  const [revision, refresh] = useState({ id: 0, force: false }),
    [localParameters, setParameters] = useState<SourceParameters>({}),
    [state, setState] = useState<{
      key: string;
      context?: Context;
      hash?: string;
      error?: string;
    }>();
  const suppliedParameters = localParameters;
  const sourceParameters = Object.fromEntries(
    Object.entries(suppliedParameters).filter(([id]) =>
      project.dynamicSources?.some((source) => source.id === id),
    ),
  );
  useEffect(() => {
    if (JSON.stringify(suppliedParameters) === JSON.stringify(sourceParameters))
      return;
    setParameters(sourceParameters);
  }, [
    JSON.stringify(suppliedParameters),
    JSON.stringify(project.dynamicSources?.map((source) => source.id)),
  ]);
  useEffect(() => {
    setParameters({});
  }, [project.id, versionId]);
  let requested: ReturnType<typeof contextRequest> | undefined,
    parameterError: string | undefined;
  try {
    requested = enabled
      ? contextRequest(
          project,
          {
            tenantId: project.tenantId,
            subject: "host",
          },
          partId || undefined,
          { sourceParameters },
        )
      : undefined;
  } catch {
    parameterError = "Enter valid source parameters to load data.";
  }
  const needs =
      !!parameterError ||
      !!(requested && (requested.sources.length || requested.clock)),
    key = JSON.stringify([
      project,
      partId,
      demoProfile,
      revision,
      enabled,
      versionId,
      requested?.parameters,
      parameterError,
    ]),
    current = parameterError
      ? { key, error: parameterError, context: undefined, hash: undefined }
      : state?.key === key
        ? state
        : undefined;
  useEffect(() => {
    if (!needs || parameterError) return;
    const controller = new AbortController();
    // Effect replay or an immediate scope change cancels before issuing another request.
    const timer = setTimeout(() => {
      const resolve = services?.resolveContext;
      void (
        resolve
          ? resolve(
              {
                project,
                versionId,
                partId: partId || undefined,
                contextProfile: demoProfile || undefined,
                sourceParameters: requested?.parameters,
                forceRefresh: revision.force,
              },
              controller.signal,
            )
          : Promise.reject(
              new Error("Provide a context resolver for connected variables."),
            )
      )
        .then((snapshot) => {
          if (!controller.signal.aborted)
            setState({
              key,
              ...snapshot,
              context: parseContext(snapshot.context),
            });
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setState({ key, error: (error as Error).message });
        });
    }, 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [key, needs, services]);
  return {
    key,
    needs,
    current,
    context: current?.context,
    hash: current?.hash,
    sourceParameters: { ...sourceParameters, ...requested?.parameters },
    setSourceParameters: (values: SourceParameters, forceRefresh = false) => {
      setParameters(values);
      refresh((previous) => ({ id: previous.id + 1, force: forceRefresh }));
    },
    blocked: needs && (!current || !!current.error),
    refresh: () =>
      refresh((previous) => ({ id: previous.id + 1, force: true })),
  };
}
